import React, { useEffect } from 'react';

import {
    DeactivatePaperPlansInput,
    GetPaperPlanMetadataInput,
    GetPaperPlanMetadataOutput,
    GetRateMultiplierTypesOutput,
    IntegerList,
    PaperPlanMetadata
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ColumnState, CsvExportParams, GridApi } from 'ag-grid-community';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { AddPaperPlan } from 'src/components/AddPaperPlan';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { EditPaperPlan } from 'src/components/EditPaperPlan';
import { FilterInput } from 'src/components/FilterInput';
import { PaperPlanDetail } from 'src/components/PaperPlanDetail';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { DEFAULT_TOASTER_TIMEOUT, Status } from 'src/constants';
import { MULTIPLIER_NAME_MAP } from 'src/constants/rateCards';
import { ADMIN_ROLE_SET, EDITOR_ROLE_SET, VIEWER_ROLE_SET } from 'src/constants/role';
import { ROUTES, URL_PARAMS } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IPlanRowData } from 'src/types/IPaperPlan';
import { IFirstPartyCorePlan, IMultiplierType } from 'src/types/IRateCard';
import { IToast } from 'src/types/IToaster';
import { getCorePlanList, mapPaperPlanMetadataToPlanValues } from 'src/util/PaperPlans/PaperPlanUtils';
import { getCurrentDate } from 'src/util/dateTimeUtil';

import './PaperPlans.scss';
import { paperPlansColDefs } from './paperPlansColDefs';

export const PaperPlans = () => {
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const { setTopAlertMsg, setToasts, userRole, userInfo } = React.useContext(AppContext);
    const history = useHistory();
    const [filterText, setFilterText] = React.useState('');
    const [plansRowData, setPlansRowData] = React.useState<IPlanRowData[]>([]);
    const [isDataLoaded, setIsDataLoaded] = React.useState(false);
    const [agGridApi, setAgGridApi] = React.useState<GridApi>();

    const [isDeletePlanModalOpen, setIsDeletePlanModalOpen] = React.useState(false);
    const [paperPlanIdToDelete, setPaperPlanIdToDelete] = React.useState<number | null>(null);
    const [corePlanList, setCorePlanList] = React.useState<IFirstPartyCorePlan[]>([]);
    const [multiplierTypeList, setMultiplierTypeList] = React.useState<IMultiplierType[]>([]);

    useEffect(() => {
        contractConfigurationClient
            .getPaperPlanMetadata(
                GetPaperPlanMetadataInput.clone({
                    serviceIds: [1],
                    includeDraftPlans: true
                })
            )
            .subscribe(
                (response: GetPaperPlanMetadataOutput) => {
                    setIsDataLoaded(true);
                    setPlansRowData(mapResponseToRowData(response) || []);
                    setCorePlanList(getCorePlanList(response) || []);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getPaperPlanMetadata API unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );

        contractConfigurationClient.getRateMultiplierTypes().subscribe(
            (response: GetRateMultiplierTypesOutput) => {
                const multiplierList = (response.rateMultiplierTypes ?? []).map((multiplier) => ({
                    ...multiplier,
                    ...(MULTIPLIER_NAME_MAP.get(multiplier.name || '') || {})
                }));
                setMultiplierTypeList(multiplierList as IMultiplierType[]);
            },
            (error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error('getRateMultiplierTypes API Unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    }, [pathname]);

    const mapResponseToRowData = (response: GetPaperPlanMetadataOutput) => {
        return response.planMetadata?.map(
            (paperPlanMetadata: PaperPlanMetadata) =>
                mapPaperPlanMetadataToPlanValues(paperPlanMetadata) as IPlanRowData
        );
    };

    const plansColDefs = [
        ...paperPlansColDefs,
        {
            field: 'action',
            headerName: 'Actions',
            filter: false,
            cellRenderer: (params: any) => {
                return (
                    <Column alignmentVertical="center">
                        <Row spacing="200">
                            <Button
                                type="link"
                                size="small"
                                data-testid={'editPlanBtn_' + params.rowIndex}
                                href={
                                    params.data.isDraft || ADMIN_ROLE_SET.has(userRole)
                                        ? `${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.EDIT}/${params.data.paperPlanId}${ROUTES.PAPER_PLANS.STEP.SETTINGS}`
                                        : `${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.EDIT}/${params.data.paperPlanId}${ROUTES.PAPER_PLANS.STEP.DETAILS}`
                                }
                                onClick={history.push}
                                disabled={!EDITOR_ROLE_SET.has(userRole)}
                            >
                                <Icon className="button-icon" tokens={editTokens} />
                            </Button>
                            {params.data.isDraft && !params.data.isDeleted && (
                                <Button
                                    type="link"
                                    size="small"
                                    data-testid={'deletePlanBtn_' + params.rowIndex}
                                    onClick={() => handlePlanDeleteBtnClick(params.data.paperPlanId)}
                                    disabled={!EDITOR_ROLE_SET.has(userRole)}
                                >
                                    <Icon className="button-icon" tokens={deleteTokens} />
                                </Button>
                            )}
                        </Row>
                    </Column>
                );
            },
            minWidth: 120,
            maxWidth: 120
        }
    ];

    const planColSortModel: ColumnState[] = [{ colId: 'creationDate', sort: 'desc', sortIndex: 0 }];

    const setDefaultFilter = () => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [Status.Active, Status.Upcoming, Status.Draft]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    const exportPlans = () => {
        const params: CsvExportParams = {
            fileName: `paper_plans_export_${getCurrentDate()}.csv`,
            columnKeys: plansColDefs.flatMap((colDef) => (colDef.field ? [colDef.field] : []))
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const handlePlanDeleteBtnClick = (paperPlanId: number) => {
        setPaperPlanIdToDelete(paperPlanId);
        setIsDeletePlanModalOpen(true);
    };

    const handlePlanDeleteWithToast = () => {
        if (paperPlanIdToDelete === null) return;

        const deactivatePaperPlansPromise = contractConfigurationClient
            .deactivatePaperPlans(
                DeactivatePaperPlansInput.clone({
                    changeBy: userInfo.username,
                    paperPlanIds: [paperPlanIdToDelete] as IntegerList
                })
            )
            .toPromise();

        deactivatePaperPlansPromise
            .then(() => {
                setIsDataLoaded(false);

                return contractConfigurationClient
                    .getPaperPlanMetadata(GetPaperPlanMetadataInput.clone({ serviceIds: [1] }))
                    .toPromise();
            })
            .then((response: GetPaperPlanMetadataOutput) => {
                setIsDataLoaded(true);

                const toastMessage = `Draft plan deleted`;
                const toast: IToast = {
                    id: 'delete-plan-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT * 2,
                    message: toastMessage
                };

                setToasts([toast]);
                setIsDeletePlanModalOpen(false);
                setPaperPlanIdToDelete(null);

                setPlansRowData(mapResponseToRowData(response) || []);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('deactivatePaperPlans / getPaperPlanMetadata unexpected error', error.message);
                setTopAlertMsg(error.message);
            });
    };

    const plansContent = (
        <Column className="paper-plans-div" spacing="300" width="100%" height="100%" minHeight={480}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <div>
                        <Button type="link" size="small" onClick={exportPlans}>
                            <Icon tokens={downloadLargeTokens} />
                            Download
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            href={`${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.ADD}${ROUTES.PAPER_PLANS.STEP.SETTINGS}`}
                            onClick={history.push}
                            disabled={!EDITOR_ROLE_SET.has(userRole)}
                        >
                            <Icon tokens={plusCircleTokens} />
                            Add plan
                        </Button>
                    </div>
                </Row>
            </Box>
            <div className="paper-plans-grid">
                <BaseAgGrid
                    columnDefs={plansColDefs}
                    sortModel={planColSortModel}
                    setAgGridApi={setAgGridApi}
                    rowData={plansRowData}
                    filterText={filterText}
                    onFirstDataRendered={setDefaultFilter}
                    loading={!isDataLoaded}
                />
            </div>

            <ConfirmationModal
                isOpen={isDeletePlanModalOpen}
                onClose={() => setIsDeletePlanModalOpen(false)}
                title="Confirm delete"
                body="Are you sure you want to delete this plan? All entered information will be lost."
                showComments={false}
                primaryButtonText="Yes, delete it"
                primaryButtonOnClick={handlePlanDeleteWithToast}
                secondaryButtonText="Cancel"
                secondaryButtonOnClick={() => setIsDeletePlanModalOpen(false)}
            />
        </Column>
    );

    return (
        <div className="paper-plans-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {plansContent}
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={EDITOR_ROLE_SET} path={`${path}${ROUTES.PAPER_PLANS.ADD}`}>
                    <AddPaperPlan corePlanList={corePlanList} multiplierTypeList={multiplierTypeList} />
                </ProtectedRoute>
                <ProtectedRoute
                    requiredRoleSet={EDITOR_ROLE_SET}
                    path={`${path}${ROUTES.PAPER_PLANS.EDIT}${URL_PARAMS.PAPER_PLAN_ID}`}
                >
                    <EditPaperPlan corePlanList={corePlanList} multiplierTypeList={multiplierTypeList} />
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={VIEWER_ROLE_SET} path={`${path}${URL_PARAMS.PAPER_PLAN_ID}`}>
                    <PaperPlanDetail />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
